import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRecoilValue } from "recoil";
import Div100vh from "react-div-100vh";
import { userDataAtom } from "@/recoil/atom/userDataAtom";
import { updateFunction, validateTenantData, trackBtnClick } from "@/lib/helper";
import PrimaryButton from "@/components/common/buttons/PrimaryButton";
import TickCircleIcon from "@/assets/icons/tick-circle.svg";
import PencilIcon from "@/assets/icons/pencil-pen.svg";
import Header from "../common/Header/header";

export default function TenantDetailsScreen() {
  const navigate = useNavigate();
  const userData = useRecoilValue(userDataAtom);

  const [loading, setLoading] = useState("");
  const [error, setError] = useState("");
  const [count, setCount] = useState(0);
  const [openForms, setOpenForms] = useState([]);
  const [tenantData, setTenantData] = useState([]);

  useEffect(() => {
    setError("");
  }, [tenantData]);

  useEffect(() => {
    setOpenForms((prevOpenForms) => {
      if (count > prevOpenForms.length) {
        return [
          ...prevOpenForms,
          ...Array(count - prevOpenForms.length).fill(false),
        ];
      } else {
        return prevOpenForms.slice(0, count);
      }
    });

    setTenantData((prevTenantData) => {
      if (count > prevTenantData.length) {
        return [
          ...prevTenantData,
          ...Array(count - prevTenantData.length).fill({
            name: "",
            phone: "",
          }),
        ];
      } else {
        return prevTenantData.slice(0, count);
      }
    });
  }, [count]);

  const handleToggleForm = (index) => {
    const updatedOpenForms = [...openForms];
    updatedOpenForms[index] = !updatedOpenForms[index];
    setOpenForms(updatedOpenForms);
  };

  const handleNextClick = async () => {
    if (validateTenantData(tenantData, setError)) {
      try {
        setLoading(true);
        await updateFunction(tenantData, "property/add-flatmates");
        trackBtnClick("Next", "Tenant Details Screen");
        navigate("/invite");
      } catch (err) {
        setError(err.message || "Something went wrong!");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleInputChange = (index, name, value) => {
    const updatedTenantData = [...tenantData];
    updatedTenantData[index] = {
      ...updatedTenantData[index],
      [name]: value,
    };
    setTenantData(updatedTenantData);
  };

  const handleFormSubmit = (index) => {
    handleToggleForm(index);
  };

  return (
    <Div100vh className="flex flex-col justify-between p-8 overflow-y-scroll no-scrollbar">
      <div className="flex flex-col se:gap-5 sm:gap-8 ">
        <Header progress="3" total="4" />
        <div className="flex flex-col gap-2">
          <span className="heading">No. of tenants splitting the rent</span>
        </div>
        <div className="flex justify-center gap-2">
          <div className="flex w-fit bg-[#222222] text-2xl p-2 px-4 gap-12">
            <button
              onClick={() =>
                setCount((prevState) => Math.max(prevState - 1, 0))
              }
            >
              -
            </button>
            <span className="w-4">{count + 1}</span>
            <button
              onClick={() =>
                setCount((prevState) => Math.min(prevState + 1, 5))
              }
            >
              +
            </button>
          </div>
        </div>
      </div>
      <div className="flex flex-col justify-center gap-2 mb-4">
        <div className="mt-2 flex w-full justify-between items-center p-2 text-lg text-[#999999] bg-[#222222]">
          <div className="w-full flex items-center justify-between">
            <div className="flex gap-2 items-center">
              <span className="font-semibold">{userData.firstName}</span>
              <span className="text=[#555555] text-sm">(You)</span>
            </div>
            <img src={TickCircleIcon} alt="tick" className="max-w-6" />
          </div>
        </div>
        {Array.from({ length: count }).map((_, index) => (
          <div key={index} className="mt-2">
            {!openForms[index] && (
              <button
                onClick={() => handleToggleForm(index)}
                className={`flex w-full justify-between items-center p-2 text-lg text-[#999999] bg-[#222222]`}
              >
                {tenantData[index]?.name ? (
                  <div className="w-full flex justify-between">
                    <span className="font-semibold">
                      {tenantData[index].name}
                    </span>
                    <div className="flex gap-8">
                      <img src={PencilIcon} alt="pencil" />
                      <img
                        src={TickCircleIcon}
                        alt="tick"
                        className="max-w-6"
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <span>Add Tenant {index + 2}</span>
                    <span>+</span>
                  </>
                )}
              </button>
            )}
            {openForms[index] && (
              <>
                <div
                  className={`flex w-full justify-between items-center p-4 text-lg text-[#BBBBBB]`}
                >
                  <span>Tenant {index + 2}</span>
                  <button
                    onClick={() => handleFormSubmit(index)}
                    className="border border-[#13C06D] border-opacity-75 px-4 py-2 text-sm text-[#13C06D] opacity-70"
                  >
                    Save
                  </button>
                </div>
                <div className="space-y-4 p-4 bg-[#0F0F0F]">
                  <div className="relative flex flex-col h-14">
                    <input
                      autoComplete="off"
                      type="text"
                      name="name"
                      required
                      value={tenantData[index]?.name || ""}
                      onChange={(e) =>
                        handleInputChange(index, "name", e.target.value)
                      }
                      className={`border p-4 text-base border-primary-500 bg-input-bg focus:outline-none w-full text-prima focus-within:border-primary-200 h-full placeholder-transparent peer `}
                      placeholder="Enter Full Name"
                    />
                    <label className="absolute left-3 transition-all duration-200 text-primary-500 peer-focus:text-primary-200 peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-gray-500 peer-focus:-top-2 peer-empty:-top-3 peer-focus:text-sm peer-focus:px-1 peer-empty:px-1 peer-focus:bg-input-bg peer-empty:bg-input-bg ">
                      Full Name
                    </label>
                  </div>
                  <div className="flex items-center border border-primary-500 p-2 text-primary-100 h-16 focus-within:border-primary-100">
                    <span className="font-sans px-2 text-white">+91</span>
                    <div className="border-l border-primary-500 h-6 mx-2"></div>
                    <input
                      autoComplete="off"
                      type="number"
                      name="phone"
                      inputMode="numeric"
                      placeholder="Enter Phone Number"
                      className="bg-transparent focus:outline-none w-full text-primary-100 text-base"
                      maxLength={10}
                      value={tenantData[index]?.phone || ""}
                      onChange={(e) =>
                        handleInputChange(index, "phone", e.target.value)
                      }
                      onInput={(e) => {
                        if (e.target.value.length > 10) {
                          e.target.value = e.target.value.slice(0, 10);
                        }
                      }}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
      <PrimaryButton
        text={loading ? "Loading..." : "Next"}
        handleClick={handleNextClick}
        error={error}
        disabled={loading}
      />
    </Div100vh>
  );
}
